<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit, { utils } from '@pocketprep/ui-kit'
import type { TIconType } from '@pocketprep/ui-kit'
import SectionHead from '@/components/Stats/SectionHead.vue'
import { monthNames } from '@/utils'
import ExamActivitySidePanel from '@/components/SidePanels/ExamActivity.vue'
import MockExamActivitySidePanel from '@/components/SidePanels/MockExamActivity.vue'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import type { Study } from '@pocketprep/types'
import { assignmentModule } from '@/store/assignment/module'
import { analyticsModule } from '@/store/analytics/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { studentModule } from '@/store/student/module'

type TCheckboxOptions = {
    id: string
    label: string
    enabled: boolean
}

type TStudyActivity = {
    icon: TIconType
    name: string
    assignmentName?: string
    iconColor: string
    date: string
    year?: string
    score: number | string
    scoreRange: string
    answers: Study.Cloud.IQuizAnswer[]
    durationSeconds: number
    quizId: string
    levelUpQuizProgress?: Study.Class.LevelUpProgress
}

const {
    Icon,
    FilterDropdown,
    TablePagination,
    Pill,
    EmptyState,
    OverflowTooltip,
} = UIKit

const OverflowToolip = OverflowTooltip

const isShowingExamActivitySidePanel = ref(false)
// Controlling mock exam separately
const isShowingMockExamActivitySidePanel = ref(false)

const currentPage = ref(0)
const perPage = ref(8)
const examActivity = ref<TStudyActivity | null>(null)

const checkboxOptions = ref<TCheckboxOptions[]>([
    {
        id: 'qotd',
        label: 'Question of the Day',
        enabled: false,
    },
    {
        id: 'quick10',
        label: 'Quick 10',
        enabled: false,
    },
    {
        id: 'timed',
        label: 'Timed Quiz',
        enabled: false,
    },
    {
        id: 'missed',
        label: 'Missed Questions',
        enabled: false,
    },
    {
        id: 'weakest',
        label: 'Weakest Subject',
        enabled: false,
    },
    {
        id: 'custom',
        label: 'Build Your Own',
        enabled: false,
    },
    {
        id: 'levelUp',
        label: 'Level Up',
        enabled: false,
    },
    {
        id: 'mockExam',
        label: 'Mock Exam',
        enabled: false,
    },
])

const currentStudentStats = computed(() => statsModule.getters.getCurrentStudentStats())

const currentStudentName = computed(() => {
    return `${currentStudentStats.value.userInfo.firstName} ${currentStudentStats.value.userInfo.lastName}`
})

const isShowingStudentQuestionReview = computed(() => {
    return route.matched.some(r => r.name === 'student-stats') && !!route.query.questionSerial
})

const getScoreRange = (score: number) => {
    if (score <= 50) {
        return 'low'
    } else if (score < 75) {
        return 'medium'
    } else {
        return 'high'
    }
}

const removeFilter = (filterId: string) => {
    checkboxOptions.value = checkboxOptions.value.map(o => o.id === filterId ? { ...o, enabled: false } : o)
}

const isRomanNumeral = (s: string): boolean => {
    if (!s) {
        return false
    }
    return /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/i.test(s)
}

const extractMockExamNumberSuffix = (name: string): string | null => {
    const tokens = name.split(' ').filter(Boolean)
    const lastToken = tokens[tokens.length - 1]
    if (!lastToken) {
        return null
    }
    if (/^\d+$/.test(lastToken) || isRomanNumeral(lastToken)) {
        return lastToken
    }
    return null
}

const getMockExamActivityName = (quizId: string) => {
    const quiz = studentModule.getters.getCurrentStudentQuizById(quizId)
    const mockExams = examModule.getters.getMockExams()
    const mockExamForQuiz = mockExams.find(me => me.objectId === quiz?.mockExam?.objectId)

    if (mockExamForQuiz) {
        const mockExamName = mockExamForQuiz.name
        const suffix = extractMockExamNumberSuffix(mockExamName)
        if (suffix) {
            return `Mock Exam ${suffix}`
        }
        return 'Mock Exam'
    }
}

const enabledFilters = computed(() => {
    return checkboxOptions.value.reduce((acc, option) => {
        if (!option.enabled) {
            return acc
        }

        acc.push(option)
        return acc
    }, [] as TCheckboxOptions[])
})

const assignments = computed((): Record<string, Study.Class.PrebuiltQuizJSON> => {
    return assignmentModule.getters.getCurrentOrgAssignments()
})

const studyActivities = computed(() => {
    const enabledFilterIds = enabledFilters.value.map(f => f.id)
    const currStudentStats = currentStudentStats.value.allActivity
    const sortedActivities = currStudentStats
        .sort((a, b) => new Date(b.quizCreatedAt).getTime() - new Date(a.quizCreatedAt).getTime())

    return sortedActivities.reduce<TStudyActivity[]>((acc, a) => {
        if (enabledFilters.value.length) {
            if (
                (!enabledFilterIds.includes('qotd') && a.mode === -1)
                || (!enabledFilterIds.includes('quick10') && a.mode === 0)
                || (!enabledFilterIds.includes('missed') && a.mode === 2)
                || (!enabledFilterIds.includes('weakest') && a.mode === 3)
                || (!enabledFilterIds.includes('timed') && a.mode === 4)
                || (!enabledFilterIds.includes('mockExam') && a.mode === 5)
                || (!enabledFilterIds.includes('levelUp') && (a.mode === 6 || a.mode === 7))
                || (!enabledFilterIds.includes('custom') && a.mode === 10)
                || (!enabledFilterIds.includes('assignment') && a.mode === 11)) {
                return acc
            }
        }
        const { icon, name, iconColor } = a.mode === 1 // mode 1 = legacy
            ? utils.studyModes[0]
            : a.mode in utils.studyModes
                ? utils.studyModes[a.mode]
                : {
                    icon: 'pencil' as TIconType,
                    name: 'Unknown Quiz',
                    iconColor: BrandColors.slate,
                }
        const mockExamName = a.mode === 5
            ? getMockExamActivityName(a.quizId)
            : null
        const date = new Date(a.date)

        const answers = a.answers || []

        const dateMonth = monthNames[date.getMonth()]?.substring(0, 3) || ''
        const dateDay = date.getDate() || ''
        const dateYear = date.getFullYear() || ''
        const assignment = a.mode === 11 && a.assignment && assignments.value[a.assignment.objectId]
        const assignmentName = assignment ? assignment.name : ''
        const levelUpProgress = a.mode === 6 && a.levelUpQuizProgress ? a.levelUpQuizProgress : undefined

        acc.push({
            icon,
            name: mockExamName || name,
            assignmentName,
            iconColor: iconColor || '',
            date: `${dateMonth} ${dateDay}`,
            year: `${dateYear}`,
            score: name === 'Question of the Day' ? (a.score === 1 ? 'correct' : 'incorrect') : a.score,
            scoreRange: getScoreRange(a.score),
            answers,
            durationSeconds: a.duration || 0,
            quizId: a.quizId,
            levelUpQuizProgress: levelUpProgress,
        })

        return acc
    }, [])
})

const pageLastIndex = computed(() => {
    const lastIndex = (currentPage.value * perPage.value) + perPage.value

    return lastIndex > studyActivities.value.length
        ? studyActivities.value.length
        : lastIndex
})

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const getLevelUpActivityInfo = (levelUpInfo: Study.Class.LevelUpProgress) => {
    const currentGroupExam = examModule.getters.getCurrentGroupExam()
    const examMetadataId = currentGroupExam?.examMetadata?.objectId

    if (!examMetadataId || !levelUpInfo.subjectId) {
        return ''
    }

    const subjectWithLevels = examModule.getters.getSubjectWithLevels(
        examMetadataId,
        levelUpInfo.subjectId
    )

    if (!subjectWithLevels) {
        return ''
    }

    return `${levelUpInfo.level}/${subjectWithLevels.levels.length} ${subjectWithLevels.subjectName}`
}

const route = useRoute()
const router = useRouter()

const showExamActivitySidePanel = (activity: TStudyActivity) => {
    examActivity.value = activity
    if (activity.name === 'Mock Exam') {
        isShowingMockExamActivitySidePanel.value = true
    } else {
        isShowingExamActivitySidePanel.value = true
    }
}

const openQuestionView = (activity: TStudyActivity) => {
    // If the activity is QotD, we want to open question view
    router.push({
        name: route.name as string,
        params: { ...route.params },
        query: {
            questionSerial: activity.answers[0]?.questionSerial,
            type: 'answered',
            quizId: activity.quizId,
        },
    })
    analyticsModule.actions.amplitudeTrack('List Item Clicked', {
        listItemContext: 'Student Stats Study Activity',
        listItemValue: activity.name,
    })
}

const closeExamActivitySidePanel = () => {
    isShowingExamActivitySidePanel.value = false
    isShowingMockExamActivitySidePanel.value = false
}

watch(currentStudentStats, () => {
    // Every time we change students, reset the page to 0
    currentPage.value = 0
})

watch(() => enabledFilters.value.length, () => {
    currentPage.value = 0
})

onMounted(async () => {
    await Promise.all([
        statsModule.actions.fetchCurrentStudentStats(),
        examModule.actions.fetchCurrentGroupExam(),
        examModule.actions.fetchExamMetadata(),
        examModule.actions.fetchMockExams(),
    ])

    await examModule.actions.fetchSubjectsWithLevels()
})
</script>

<template>
    <div
        class="activities"
        :class="{ 'activities--loading': currentStudentStats.isLoading }"
    >
        <ExamActivitySidePanel
            v-if="isShowingExamActivitySidePanel && examActivity"
            :trap-focus="!isShowingStudentQuestionReview"
            :student="currentStudentName"
            :exam-activity="examActivity"
            @close="closeExamActivitySidePanel"
        />
        <MockExamActivitySidePanel
            v-else-if="isShowingMockExamActivitySidePanel && examActivity"
            :trap-focus="!isShowingStudentQuestionReview"
            :student="currentStudentName"
            :exam-activity="examActivity"
            @close="closeExamActivitySidePanel"
        />
        <SectionHead
            title="Study Activity"
            icon="activity"
            class="activities__section-head"
        >
            <FilterDropdown
                v-if="studyActivities.length || (studyActivities.length === 0 && enabledFilters.length)"
                class="activities__filter"
                filterLabel="Filter"
                filterOptionsLabel="FILTER BY QUIZ MODE"
                :checkboxEntries="checkboxOptions"
                :radioEntries="[]"
            />
        </SectionHead>
        <div
            v-if="studyActivities.length"
            class="activities__activity-list"
        >
            <div
                v-if="enabledFilters.length"
                class="activities__filters-label-container"
            >
                <div class="activities__filters-label">
                    Filters:
                </div>
                <Pill
                    v-for="filter in enabledFilters"
                    :key="filter.id"
                    :show-close="true"
                    @click="removeFilter(filter.id)"
                >
                    {{ filter.label }}
                </Pill>
            </div>
            <div
                v-for="(activity, index) in studyActivities.slice(currentPage * perPage, pageLastIndex)"
                :key="index"
                class="activities__activity"
                :class="{
                    'activities__activity--level-up': (activity.name === 'Level Up' && activity.levelUpQuizProgress)
                        || activity.name === 'Recall Challenge',
                    'activities__activity--assignment': activity.name === 'Assignment' && activity.assignmentName
                }"
                tabindex="0"
                :aria-label="`${activity.name} ${activity.date} ${activity.score}`"
                @click="activity.name !== 'Question of the Day' ? showExamActivitySidePanel(activity)
                    : openQuestionView(activity)"
                @mousedown.prevent
            >
                <div
                    class="activities__activity-left"
                    :class="{
                        'activities__activity-left--level-up':
                            (activity.name === 'Level Up' && activity.levelUpQuizProgress)
                            || activity.name === 'Recall Challenge',
                        'activities__activity-left--assignment':
                            activity.name === 'Assignment' && activity.assignmentName
                    }"
                    aria-hidden="true"
                >
                    <div class="activities__activity-info">
                        <div class="activities__activity-icon">
                            <Icon :type="activity.icon" :style="{ color: activity.iconColor }" />
                        </div>
                        <div class="activities__activity-name">
                            <OverflowToolip>
                                {{ activity.name === 'Recall Challenge' ? 'Level Up' : activity.name }}
                            </OverflowToolip>
                        </div>
                    </div>
                    <div
                        v-if="activity.name === 'Level Up'
                            && activity.levelUpQuizProgress
                            && activity.levelUpQuizProgress.level
                            && activity.levelUpQuizProgress.subjectName
                        "
                        class="activities__level-up-info"
                    >
                        {{ getLevelUpActivityInfo(activity.levelUpQuizProgress) }}
                    </div>
                    <div
                        v-if="activity.name === 'Recall Challenge'"
                        class="activities__level-up-info"
                    >
                        Recall Challenge
                    </div>
                    <div
                        v-if="activity.name === 'Assignment' && activity.assignmentName"
                        class="activities__assignment-name"
                    >
                        {{ activity.assignmentName }}
                    </div>
                </div>
                <div
                    class="activities__activity-date"
                    :class="{
                        'activities__activity-date--level-up':
                            activity.name === 'Level Up' && activity.levelUpQuizProgress,
                        'activities__activity-date--assignment':
                            activity.name === 'Assignment' && activity.assignmentName
                    }"
                    aria-hidden="true"
                >
                    {{ activity.date }}
                </div>
                <div
                    class="activities__activity-score-container"
                    :class="{
                        'activities__activity-score-container--level-up':
                            activity.name === 'Level Up' && activity.levelUpQuizProgress ,
                        'activities__activity-score-container--assignment':
                            activity.name === 'Assignment' && activity.assignmentName
                    }"
                    aria-hidden="true"
                >
                    <div
                        class="activities__activity-score"
                        :class="{
                            [`activities__activity-score--${activity.scoreRange}`]:
                                activity.name !== 'Question of the Day',
                            [`activities__activity-score--${activity.score}`]:
                                activity.name === 'Question of the Day'
                        }"
                    >
                        <template v-if="activity.name === 'Question of the Day'">
                            <Icon
                                v-if="activity.score === 'correct'"
                                type="correct"
                                class="activities__correct"
                                title="Correct"
                            />
                            <Icon
                                v-else
                                type="incorrect"
                                class="activities__incorrect"
                                title="Incorrect"
                            />
                        </template>
                        <template v-else>
                            {{ activity.score }}%
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-else
            class="activities__no-activity"
        >
            <div
                v-if="enabledFilters.length"
                class="activities__filters-label-container"
            >
                <div class="activities__filters-label">
                    Filters:
                </div>
                <Pill
                    v-for="filter in enabledFilters"
                    :key="filter.id"
                    :show-close="true"
                    @click="removeFilter(filter.id)"
                >
                    {{ filter.label }}
                </Pill>
            </div>
            <EmptyState
                class="activities__empty-state"
                title="There is no study activity."
                message="Once students start studying, you will be able to see their activity."
            />
        </div>
        <TablePagination
            v-if="studyActivities.length > perPage"
            class="activities__pagination"
            unit="Quizzes"
            :per-page="perPage"
            :current-page="currentPage"
            :total="studyActivities.length"
            @changePage="changePage"
        />
    </div>
</template>

<style lang="scss" scoped>


.activities {
    background: $white;
    border-radius: 6px;
    box-sizing: border-box;
    min-height: 420px;
    display: flex;
    position: relative;
    flex-direction: column;

    &__section-head {
        &:deep(.section-head__title-icon ) {
            margin: 0 9px 0 2px !important;
        }
    }

    &__filter {
        position: relative;
    }

    &__filters-label-container {
        padding: 6px 0 6px 0;
    }

    &__filters-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate-03;
        margin-right: 9px;
        display: inline-block;
        margin-left: 2px;
        vertical-align: top;
    }

    &__activity-list {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    &__activity {
        display: flex;
        width: 100%;
        justify-content: space-between;
        min-height: 42px;
        position: relative;
        align-items: center;
        cursor: pointer;
        outline: none;
        box-sizing: border-box;

        &::before {
            content: '';
            position: absolute;
            background: rgba($gray-divider, 0.5);
            left: 39px;
            bottom: -1px;
            height: 1px;
            width: calc(100% - 39px);
            z-index: 1;
        }

        &:focus {
            &::after {
                content: '';
                position: absolute;
                left: -10px;
                top: 1px;
                width: calc(100% + 20px);
                height: calc(100% - 3px);
                border: 1px solid $brand-blue;
                border-radius: 6px;
            }
        }

        &:hover {
            background-color: $gray-background;
        }

        &--level-up, &--assignment {
            min-height: 62px;
        }
    }

    &__activity-left {
        display: flex;
        align-items: center;
        width: 240px;

        &--level-up, &--assignment {
            display: block;
        }
    }

    &__activity-info {
        display: flex;
        align-items: center;
    }

    &__activity-icon {
        width: 25px;
        height: 24px;
        margin-right: 8px;

        svg {
            width: 100%;
        }
    }

    &__activity-name {
        font-size: 14px;
        line-height: 16px;
    }

    &__level-up-info, &__assignment-name {
        color: $slate;
        font-size: 14px;
        line-height: 19px;
        margin-left: 34px;
        display: -webkit-box;
        width: 175px;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        overflow: hidden;
    }

    &__activity-date {
        color: $slate-01;
        font-size: 14px;
        line-height: 17px;
        text-align: right;
        width: 70px;

        &--level-up, &--assignment {
            margin-bottom: 16px;
        }
    }

    &__activity-score-container {
        width: 86px;
        display: flex;
        justify-content: flex-end;

        &--level-up, &--assignment {
            margin-bottom: 16px;
        }
    }

    &__activity-score {
        border-radius: 3px;
        height: 24px;
        padding: 0 5px;
        font-weight: 600;
        display: flex;
        align-items: center;
        font-size: 14px;

        &--low {
            background: $red-hint;
        }

        &--medium {
            background: $cream-puff;
        }

        &--high {
            background: $barely-green;
        }

        &--correct,
        &--incorrect {
            background: transparent !important;
            display: flex;
            justify-content: flex-end;
        }

        &--correct {
            margin-right: -4px;
            color: $spectral-green;
            width: 48px;
        }

        &--incorrect {
            margin-right: -4px;
            width: 48px;
            color: $red-pegasus;
        }
    }

    &__correct {
        width: 22px;
        height: 22px;
        color: $cadaverous;
    }

    &__incorrect {
        height: 22px;
        width: 22px;
        color: $pepper;
    }

    &__pagination {
        margin-left: auto;
        margin-right: 0;
        height: 48px;
    }
}
</style>
